import React, { useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import apiService from '../services/apiService';

const defaultForm = {
  labelIt: '',
  labelEn: '',
  aliasesItText: '',
  aliasesEnText: '',
  isActive: true,
};

const parseAliasesInput = (value) => {
  const parts = String(value || '').split(/[\n,]/g);
  const dedup = new Set();
  const aliases = [];

  for (const part of parts) {
    const alias = part.trim().toLowerCase();
    if (!alias || dedup.has(alias)) continue;
    dedup.add(alias);
    aliases.push(alias);
  }

  return aliases;
};

const renderAliases = (aliases) => {
  if (!Array.isArray(aliases) || aliases.length === 0) {
    return <span style={{ color: '#94a3b8' }}>-</span>;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {aliases.map((alias) => (
        <span
          key={alias}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 500,
            background: '#f1f5f9',
            color: '#334155',
            border: '1px solid #e2e8f0'
          }}
        >
          {alias}
        </span>
      ))}
    </div>
  );
};

const ProfileRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getProfileRoles();
      setRoles(response?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setFormOpen(true);
  };

  const openEdit = (role) => {
    setEditingId(role.id);
    setForm({
      labelIt: role.label_it || '',
      labelEn: role.label_en || '',
      aliasesItText: Array.isArray(role.aliases_it) ? role.aliases_it.join('\n') : '',
      aliasesEnText: Array.isArray(role.aliases_en) ? role.aliases_en.join('\n') : '',
      isActive: role.isActive !== false,
    });
    setFormOpen(true);
  };

  const save = async () => {
    const payload = {
      labelIt: form.labelIt.trim(),
      labelEn: form.labelEn.trim(),
      aliasesIt: parseAliasesInput(form.aliasesItText),
      aliasesEn: parseAliasesInput(form.aliasesEnText),
      isActive: Boolean(form.isActive),
    };

    if (!payload.labelIt) {
      alert('La label italiana è obbligatoria');
      return;
    }

    if (!payload.labelEn) {
      alert('La label inglese è obbligatoria');
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await apiService.updateProfileRole(editingId, payload);
      } else {
        await apiService.createProfileRole(payload);
      }
      setFormOpen(false);
      await loadRoles();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (role) => {
    try {
      await apiService.updateProfileRole(role.id, { isActive: !role.isActive });
      await loadRoles();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  return (
    <div>
      <div className="card">
        <h2>Profile Roles</h2>
        <p>
          Gestisci il catalogo globale dei ruoli professionali selezionabili nel profilo utente.
          Ogni ruolo deve avere label italiana e inglese. Gli alias servono esclusivamente
          per facilitare la ricerca.
        </p>
        <button className="btn btn-primary" onClick={openCreate} style={{ marginTop: 12 }}>
          + Nuovo Ruolo
        </button>
      </div>

      {error && <div className="card" style={{ color: 'red' }}>❌ {error}</div>}

      {formOpen && (
        <div className="card">
          <h3>{editingId ? 'Modifica Ruolo' : 'Nuovo Ruolo'}</h3>
          <div style={{ display: 'grid', gap: 10, maxWidth: 560 }}>
            <label>
              Label italiana *
              <input
                className="form-input"
                value={form.labelIt}
                onChange={(e) => setForm((prev) => ({ ...prev, labelIt: e.target.value }))}
                placeholder="es. Cuoco/a"
              />
            </label>

            <label>
              Label inglese *
              <input
                className="form-input"
                value={form.labelEn}
                onChange={(e) => setForm((prev) => ({ ...prev, labelEn: e.target.value }))}
                placeholder="es. Cook"
              />
            </label>

            <label>
              Alias italiani (facoltativi)
              <textarea
                className="form-input"
                rows={3}
                value={form.aliasesItText}
                onChange={(e) => setForm((prev) => ({ ...prev, aliasesItText: e.target.value }))}
                placeholder={'Un alias per riga o separati da virgola\nEs: manutentore\ntecnico manutenzione'}
              />
            </label>

            <label>
              Alias inglesi (facoltativi)
              <textarea
                className="form-input"
                rows={3}
                value={form.aliasesEnText}
                onChange={(e) => setForm((prev) => ({ ...prev, aliasesEnText: e.target.value }))}
                placeholder={'Un alias per riga o separati da virgola\nEs: maintenance\nmaintenance technician'}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              Ruolo attivo
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
              <button className="btn" onClick={() => setFormOpen(false)} disabled={saving}>Annulla</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? (
          <p>Caricamento...</p>
        ) : roles.length === 0 ? (
          <p>Nessun ruolo configurato.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}>IT</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}>EN</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}>Alias IT</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}>Alias EN</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}>Stato</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', borderBottom: '1px solid #eee' }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td style={{ padding: '8px 4px' }}>{role.label_it}</td>
                  <td style={{ padding: '8px 4px', color: '#334155' }}>{role.label_en}</td>
                  <td style={{ padding: '8px 4px' }}>{renderAliases(role.aliases_it)}</td>
                  <td style={{ padding: '8px 4px' }}>{renderAliases(role.aliases_en)}</td>
                  <td style={{ padding: '8px 4px' }}>
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={role.isActive ? 'Disattiva ruolo' : 'Attiva ruolo'}
                      title={role.isActive ? 'Disattiva ruolo' : 'Attiva ruolo'}
                      onClick={() => toggleActive(role)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleActive(role);
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 92,
                        padding: '6px 12px',
                        borderRadius: 999,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#fff',
                        background: role.isActive
                          ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
                          : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)'
                      }}
                    >
                      {role.isActive ? 'Attivo' : 'Disattivo'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                    <button className="btn icon-btn" title="Modifica" onClick={() => openEdit(role)}>
                      <EditIcon fontSize="small" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProfileRoles;
